import React, { useState, useRef, useEffect, useMemo } from "react";
import { MinimizeIcon, MaximizeIcon, CloseIcon, FullscreenIcon } from "./icons";
import {
  preventEventPropagation,
  isInteractiveElement,
  getClientCoordinates,
  postSize,
  getIOStyle,
} from "./helper";
import "./style.css";

const BytePlusFloatingWidget = ({
  streamUrl,
  apiKey,
  onOpenStream = () => {},
  handleClosePreview = () => {},
  isLive = true,
  mainContainerRef = ".system-container",
  showMinimizeButton = true,
  showMaximizeButton = false,
  showCloseButton = false,
  children: player = null,
  isFullScreenPlayer = false,
}) => {
  // states
  const [minimized, setMinimized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState(() => {
    // When API key is present, position relative to viewport, otherwise use default
    return apiKey ? { x: 20, y: 20 } : { x: 20, y: 20 };
  });
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [containerRect, setContainerRect] = useState(null);
  //   reference to the preview element
  const previewRef = useRef(null);
  const containerRef = useRef(null);
  const Y_PADDING = 12;

  const measureContainer = () => {
    // If API key is present, use body as container, otherwise use the specified mainContainerRef
    const containerSelector = apiKey ? "body" : mainContainerRef;
    const el = document?.querySelector(containerSelector);
    if (el) {
      containerRef.current = el;
      if (apiKey) {
        // For body container, use viewport dimensions
        setContainerRect({
          left: 0,
          top: 0,
          right: window.innerWidth,
          bottom: window.innerHeight,
          width: window.innerWidth,
          height: window.innerHeight,
        });
      } else {
        const rect = el?.getBoundingClientRect();
        setContainerRect(rect);
      }
    }
  };

  const handleMouseDown = (e) => {
    if (isInteractiveElement(e?.target)) {
      return;
    }

    preventEventPropagation(e);
    if (previewRef?.current) {
      const coords = getClientCoordinates(e);
      const rect = previewRef?.current?.getBoundingClientRect();
      setDragOffset({
        x: coords.clientX - rect?.left,
        y: coords.clientY - rect?.top,
      });
      setIsDragging(true);
    }
  };

  const handleTouchStart = (e) => {
    const target = e?.touches?.[0] ? e?.target : e?.target;

    if (isInteractiveElement(target)) {
      return;
    }
    preventEventPropagation(e);
    handleMouseDown(e);
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      if (!containerRect || !previewRef?.current) return;

      const coords = getClientCoordinates(e);
      // Desired viewport position
      const rawX = coords.clientX - dragOffset?.x;
      const rawY = coords.clientY - dragOffset?.y;

      // Clamp to container bounds in viewport coordinates
      const widgetRect = previewRef?.current?.getBoundingClientRect();

      let minX, minY, maxX, maxY;
      if (apiKey) {
        // For body container, use viewport bounds
        minX = Y_PADDING;
        minY = Y_PADDING;
        maxX = window.innerWidth - widgetRect?.width - Y_PADDING;
        maxY = window.innerHeight - widgetRect?.height - Y_PADDING;
      } else {
        minX = containerRect?.left + Y_PADDING;
        minY = containerRect?.top + Y_PADDING;
        maxX = containerRect?.right - widgetRect?.width - Y_PADDING;
        maxY = containerRect?.bottom - widgetRect?.height - Y_PADDING;
      }

      const clampedX = Math.min(Math.max(minX, rawX), maxX);
      const clampedY = Math.min(Math.max(minY, rawY), maxY);

      setPosition({ x: clampedX, y: clampedY });
    }
  };

  const handleTouchMove = (e) => {
    preventEventPropagation(e);
    handleMouseMove(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleTouchEnd = (e) => {
    preventEventPropagation(e);
    handleMouseUp();
  };

  useEffect(() => {
    // Measure container on mount
    measureContainer();
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, { passive: false });
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [isDragging, dragOffset, containerRect]);

  // Keep position within container when container updates or on resize/scroll
  useEffect(() => {
    const clampIntoView = () => {
      if (apiKey) return;
      if (!containerRef?.current || !previewRef?.current) return;

      let cRect;
      if (apiKey) {
        // For body container, use viewport dimensions
        cRect = {
          left: 0,
          top: 0,
          right: window.innerWidth,
          bottom: window.innerHeight,
        };
      } else {
        cRect = containerRef?.current?.getBoundingClientRect();
      }

      const wRect = previewRef?.current?.getBoundingClientRect();
      const minX = cRect?.left + Y_PADDING;
      const minY = cRect?.top + Y_PADDING;
      const maxX = cRect?.right - wRect?.width - Y_PADDING;
      const maxY = cRect?.bottom - wRect?.height - Y_PADDING;
      setPosition((prev) => ({
        x: Math.min(Math.max(minX, prev?.x), maxX),
        y: Math.min(Math.max(minY, prev?.y), maxY),
      }));
    };

    clampIntoView();

    const onResize = () => {
      measureContainer();
      clampIntoView();
    };
    const onScroll = () => {
      measureContainer();
      clampIntoView();
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const fullScreenPlayerStyle = useMemo(() => {
    // For apiKey variant, avoid inline positioning to prevent flicker
    if (!isFullScreenPlayer || apiKey) return {};
    const containerRect = containerRef?.current?.getBoundingClientRect();
    return {
      left: `${containerRect?.left + 12}px`,
      top: "2.5%",
    };
  }, [isFullScreenPlayer, containerRect, apiKey]);

  const handleFullscreen = (e) => {
    preventEventPropagation(e);
    if (!isDragging) {
      onOpenStream(true);
    }
  };

  useEffect(() => {
    if (apiKey) {
      postSize({
        minimized: minimized, isFullScreenPlayer: isFullScreenPlayer
      });
    }
  }, [isFullScreenPlayer, apiKey, minimized, previewRef]);

  return (
    <div
      ref={previewRef}
      className={`floating-preview ${
        isFullScreenPlayer
          ? "full-screen-player " + (apiKey ? " api-key-container" : "")
          : `${isDragging ? "dragging" : ""} ${
              minimized ? "minimized-preview" : ""
            } ${
              apiKey
                ? isFullScreenPlayer
                  ? " apikey-full-screen-player"
                  : ""
                : ""
            }`
      } ${
        apiKey
          ? "apikey-prevent-hover apikey-prevent-dragging apikey-prevent-transition"
          : ""
      }`}
      style={{
        ...(apiKey ? {} : { left: `${position.x}px`, top: `${position.y}px` }),
        ...fullScreenPlayerStyle,
        ...(isFullScreenPlayer ? getIOStyle({apiKey})?.fullScreenPlayer || {} : {}),
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      //   onClick={handleClick}
    >
      <div className="preview-header">
        <div className="live-indicator">
          <span className="live-dot"></span>
          {isLive ? "LIVE" : "OFFLINE"}
        </div>
        <div className="control">
          {showMinimizeButton && !minimized && (
            <MinimizeIcon
              onClick={(e) => {
                preventEventPropagation(e);
                if (isFullScreenPlayer) {
                  onOpenStream(false);
                } else setMinimized(true);
              }}
            />
          )}
          {showMaximizeButton ||
            (minimized && (
              <MaximizeIcon
                onClick={(e) => {
                  preventEventPropagation(e);
                  setMinimized(false);
                }}
              />
            ))}
          {showCloseButton && (
            <CloseIcon
              onClick={(e) => {
                preventEventPropagation(e);
                handleClosePreview(false);
              }}
            />
          )}
        </div>
      </div>
      <div
        className={
          isFullScreenPlayer ? "full-screen-player-preview" : "preview-video"
        }
        style={isFullScreenPlayer ? getIOStyle()?.fullScreenPlayerPreview : {}}
      >
        {player ? (
          player
        ) : (
          <video
            src={streamUrl}
            muted
            autoPlay
            loop
            playsInline
            className="preview-video-element"
          />
        )}
        {!minimized && !isFullScreenPlayer && (
          <div className="preview-overlay">
            <div className="play-button">
              <FullscreenIcon onClick={handleFullscreen} />
            </div>
          </div>
        )}
      </div>
      {!isFullScreenPlayer && (
        <div className="preview-footer">
          <span className="click-hint">Click to open full stream</span>
        </div>
      )}
    </div>
  );
};

export default BytePlusFloatingWidget;
